import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { cancelarAssinaturaAsaas } from '../services/asaasService';
import { linkGerenciarAssinaturaGoogle } from '../services/canalPagamento';

// Bloco "Cancelar assinatura" dos painéis do computador (Gestor e Consultor). Mesmo comportamento do app:
// o acesso continua até o fim do período já pago; depois, relatórios gerados por plano ficam bloqueados (nada é apagado).
// Quem calcula a data e encerra o acesso é o servidor (cancelarAssinaturaAsaas + rotina agendada).
const formatarData = (iso) => {
  try { return new Date(iso).toLocaleDateString('pt-BR'); } catch (e) { return ''; }
};

export default function BlocoCancelarAssinatura({ perfil, cancelamento }) {
  const ehConsultor = perfil === 'consultor';
  const nomePerfil = ehConsultor ? 'Consultor' : 'Gestor';
  const [aberto, setAberto] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [erro, setErro] = useState('');
  const [agendadoLocal, setAgendadoLocal] = useState(null);

  const acessoAte = (cancelamento && cancelamento.acessoAte) || agendadoLocal;

  // Canal da assinatura: se foi comprada pelo Google Play, o cancelamento é feito na Play Store (não por este botão).
  const [origemGoogle, setOrigemGoogle] = useState(null); // null = ainda não sei / não é Google; { productId }
  useEffect(() => {
    const uid = auth.currentUser && auth.currentUser.uid;
    if (!uid) return;
    getDoc(doc(db, 'usuarios', uid)).then((snap) => {
      const d = snap.exists() ? snap.data() : {};
      const origem = ehConsultor ? d.consultorOrigem : d.gestorOrigem;
      const g = ehConsultor ? d.consultorGoogle : d.gestorGoogle;
      setOrigemGoogle(origem === 'google' ? { productId: (g && g.productId) || null } : null);
    }).catch(() => {});
  }, [ehConsultor]);

  const confirmar = async () => {
    const uid = auth.currentUser && auth.currentUser.uid;
    if (!uid) return;
    setCancelando(true);
    setErro('');
    try {
      const perfilSnap = await getDoc(doc(db, 'usuarios', uid));
      const dados = perfilSnap.exists() ? perfilSnap.data() : {};
      const subscriptionId = ehConsultor ? dados.consultorAsaasSubscriptionId : dados.gestorAsaasSubscriptionId;
      if (!subscriptionId) {
        setErro('Não encontramos uma assinatura ativa para cancelar. Fale com o suporte: suporte@phoenixos.com.br');
        return;
      }
      const r = await cancelarAssinaturaAsaas({ subscriptionId });
      // Sem data = o servidor encerrou na hora (ex.: pagamento em atraso); o painel reflete isso sozinho pela leitura do plano.
      setAgendadoLocal((r && r.acessoAte) || 'agora');
      setAberto(false);
    } catch (e) {
      console.error('[PHOENIX] Erro ao cancelar assinatura:', e);
      setErro('Não foi possível cancelar agora. Tente novamente ou fale com o suporte: suporte@phoenixos.com.br');
    } finally {
      setCancelando(false);
    }
  };

  return (
    <div className="p-8 bg-black/20 rounded-[2.5rem] border border-white/5">
      <p className="text-sm font-black text-white uppercase tracking-widest mb-2">Assinatura</p>
      {origemGoogle ? (
        <>
          <p className="text-[11px] text-slate-400 font-bold tracking-widest mb-4 leading-relaxed">
            Sua assinatura do plano {nomePerfil} foi contratada pela Google Play. Para cancelar ou alterar, use a Play Store (Pagamentos e assinaturas > Assinaturas).
            {acessoAte ? <> Seu acesso continua até <strong className="text-amber-200">{formatarData(acessoAte)}</strong>.</> : null}
          </p>
          <a
            href={linkGerenciarAssinaturaGoogle(origemGoogle.productId)} target="_blank" rel="noopener noreferrer"
            className="inline-block text-[11px] font-black uppercase tracking-widest text-slate-300 hover:text-lime-400 border border-white/10 hover:border-lime-400/40 px-5 py-3 rounded-full transition-all"
          >
            Gerenciar na Play Store
          </a>
        </>
      ) : acessoAte ? (
        <p className="text-xs text-amber-300 leading-relaxed">
          {acessoAte === 'agora'
            ? 'Assinatura cancelada.'
            : <>Assinatura cancelada. Seu acesso continua até <strong className="text-amber-200">{formatarData(acessoAte)}</strong>. Para reativar, use <strong className="text-white">Gerenciar Pagamento / Trocar de Plano</strong> acima.</>}
        </p>
      ) : (
        <>
          <p className="text-[11px] text-slate-500 font-bold tracking-widest mb-4">
            Cancele a renovação do plano {nomePerfil} quando quiser. Você mantém o acesso até o fim do período já pago.
          </p>
          <button
            onClick={() => { setErro(''); setAberto(true); }}
            className="text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-red-400 border border-white/10 hover:border-red-400/40 px-5 py-3 rounded-full transition-colors"
          >
            Cancelar assinatura
          </button>
        </>
      )}

      {aberto && (
        <div className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-md flex items-center justify-center p-6">
          <div className="bg-[#0a0a12] border border-white/10 rounded-[2.5rem] p-8 max-w-md w-full">
            <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-3">Cancelar assinatura?</h3>
            <p className="text-sm text-slate-400 leading-relaxed mb-3">
              Você deixa de ser cobrado nas próximas renovações do plano <strong className="text-white">{nomePerfil}</strong>.
            </p>
            <p className="text-sm text-slate-400 leading-relaxed mb-3">
              Seu acesso <strong className="text-white">continua até o fim do período que você já pagou</strong>.
            </p>
            <div className="bg-amber-950/30 border border-amber-400/20 rounded-2xl p-4 mb-6">
              <p className="text-[11px] text-amber-200 leading-relaxed">
                <strong className="text-white">O que acontece com seus relatórios:</strong>{' '}
                {ehConsultor
                  ? 'depois dessa data, a Carteira e os relatórios dos seus clientes ficam bloqueados. Nada é apagado: tudo volta a abrir se você assinar de novo.'
                  : 'depois dessa data, você continua com acesso aos relatórios que comprou pelo Entry. Os relatórios gerados durante o plano ficam bloqueados (não são apagados) e voltam a abrir se você assinar de novo. Este painel no computador também deixa de abrir.'}
              </p>
            </div>
            {erro && <p className="text-xs text-red-400 mb-4 leading-relaxed">{erro}</p>}
            <div className="flex flex-col gap-3">
              <button
                onClick={confirmar}
                disabled={cancelando}
                className="w-full bg-red-500/90 text-white py-4 rounded-full font-black uppercase text-xs disabled:opacity-50 active:scale-95 transition-all"
              >
                {cancelando ? 'Cancelando…' : 'Sim, cancelar assinatura'}
              </button>
              <button
                onClick={() => setAberto(false)}
                disabled={cancelando}
                className="w-full bg-white/5 text-white py-4 rounded-full font-black uppercase text-xs border border-white/10 active:scale-95 transition-all"
              >
                Manter assinatura
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
